import PropTypes from 'prop-types';
import { Modal } from '@components/modal/new';
import { Button } from '@components/button/new';
import styled from 'styled-components';
import { analytics } from '@analytics';
import { Box, Flex } from '@components/grid';
import { ANALYTICS_EVENTS, RECOMMENDATION_ENGINE, colors } from '@nandosaus/constants';
import { P } from '@components/typography/new';
import { TextClamp } from '@components/text-clamp';
import { useRootStore } from '@nandosaus/state-management';
import { GeneralAllergenPDFLink } from '@components/general-allergen-pdf-link';
import { ModalImageHeader } from '@components/modal/new/modal-image-header';
import { ErrorAlert } from '../ui';

const ReviewScreen = ({ modalOpen, handleModalClose, setVisibleCart, setFlowStep }) => {
  const { CartStore, RecommendationEngineStore } = useRootStore();
  const { orderItems } = RecommendationEngineStore;

  const onModalClose = () => {
    if (!RecommendationEngineStore.addedRecommendationsToCart) {
      const { recommendationsInput } = RecommendationEngineStore;
      analytics.track(ANALYTICS_EVENTS.RECOMMENDED_CART_ABANDONED, {
        cartId: CartStore.cartId,
        restaurantId: recommendationsInput.restaurantId,
        mealType: recommendationsInput.mealType,
        basting: recommendationsInput.basting,
        groupKidsNumber: recommendationsInput.group.kids,
        groupAdultsNumber: recommendationsInput.group.adults,
        subtotalPriceInCents: RecommendationEngineStore.subtotalPrice.cents,
      });
    }
    handleModalClose();
  };

  const acceptRecommendations = () => {
    RecommendationEngineStore.addRecommendationsToCart();
  };

  return (
    <Modal
      open={modalOpen}
      handleClose={onModalClose}
      isDividerHidden
      headerBackgroundColor="transparent"
      variant="side"
      header={
        <ModalImageHeader
          handleBack={() => setFlowStep(RECOMMENDATION_ENGINE.screens.quiz)}
          handleClose={onModalClose}
          modalTitle={['Take', 'your pick']}
          description="There’s no wrong choice here. And, if you change your mind, you can make edits later."
        />
      }
      footer={
        <>
          {RecommendationEngineStore.error && <ErrorAlert />}
          <Flex overflow="hidden" width="100%" gap="12px" justifyContent="center">
            <Button
              overflow="hidden"
              onClick={() => {
                acceptRecommendations();
                handleModalClose();
              }}
              widthStyle="fit"
              variant="secondary"
              size="tall"
            >
              Add items & browse
            </Button>
            <Button
              overflow="hidden"
              onClick={() => {
                acceptRecommendations();
                setVisibleCart(true);
                handleModalClose();
              }}
              widthStyle="fit"
              size="tall"
            >
              Add items & view cart
            </Button>
          </Flex>
        </>
      }
    >
      <Box pb={{ _: 4, md: 0 }}>
        <OrderItemsContainer>
          {orderItems.map(orderItem => {
            const dietOptions = orderItem.product.formattedSuitability || [];
            return (
              <StyledOrderItemFlex gap="16px" justifyContent="space-between" key={orderItem.id}>
                <Flex gap="16px">
                  <Flex
                    aria-label="quantity"
                    width="32px"
                    height="32px"
                    minWidth="32px"
                    justifyContent="center"
                    alignItems="center"
                    background="black"
                    borderRadius="100%"
                  >
                    <P color="white" variant="standard-bold">
                      {orderItem.quantity}
                    </P>
                  </Flex>

                  <ContentContainer
                    flexDirection="column"
                    height="auto"
                    alignSelf="stretch"
                    justifyContent="space-between"
                  >
                    <Box>
                      <P mb="4px" variant="medium-semi-bold">
                        {orderItem.product.name}
                      </P>
                      <P variant="small" color="greyPrimary">
                        <TextClamp lines={3}>{orderItem.product.description}</TextClamp>
                      </P>
                      {orderItem.product.formattedKilojoules && (
                        <P variant="extra-small" mt="6px" color="grey70">
                          <TextClamp>{orderItem.product.formattedKilojoules} per serve</TextClamp>
                        </P>
                      )}
                    </Box>
                    {dietOptions && dietOptions.length > 0 && (
                      <Flex mt="6px" gap="8px" variant="extra-small-semi-bold" alignItems="center">
                        {dietOptions.map(option => {
                          return (
                            <P
                              backgroundColor="warmWhite3"
                              borderRadius="4px"
                              variant="extra-small-semi-bold"
                              py="4px"
                              px="8px"
                            >
                              {option}
                            </P>
                          );
                        })}
                        <P variant="extra-small">Options</P>
                      </Flex>
                    )}
                  </ContentContainer>
                </Flex>
                <Image src={orderItem.product.image} alt={orderItem.product.description} />
              </StyledOrderItemFlex>
            );
          })}
        </OrderItemsContainer>
        <Flex height="4px" width="200%" backgroundColor="warmWhite3" mt="4px" mx="-50px" />
        <Box display="flex" flexDirection="column" gap="20px" py="32px">
          <Flex justifyContent="space-between">
            <P variant="large-bold">Sub total</P>
            <P variant="large-bold">{RecommendationEngineStore.subtotalPrice.formattedPrice}</P>
          </Flex>

          <P variant="extra-small" color="greyPrimary" textAlign="center">
            You can change your items, specify dietary requirements and add extras later from your basket.
          </P>
          <GeneralAllergenPDFLink centeredText mt="0" />

          <P variant="extra-small" textAlign="center">
            The average adult daily energy intake is 8,700kJ.
          </P>
        </Box>
      </Box>
    </Modal>
  );
};

const StyledOrderItemFlex = styled(Flex)`
  border-bottom: 1px solid ${colors.warmWhite3};
  padding: 28px 0px;

  &:first-child {
    padding-top: 0;
  }
`;

const OrderItemsContainer = styled(Box)`
  & > ${StyledOrderItemFlex}:last-child {
    border-bottom: none;
  }
`;

const Image = styled.img`
  width: 96px;
  height: 96px;
  min-width: 96px;
  object-fit: cover;
  border-radius: 16px;
  overflow: hidden;
`;

const ContentContainer = styled(Flex)`
  grid-column-start: 3;
  grid-column-end: 4;
`;

ReviewScreen.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  setFlowStep: PropTypes.func.isRequired,
  setVisibleCart: PropTypes.func.isRequired,
};

export { ReviewScreen };
